// @ts-check
/** @typedef {import('../types/api').RecoverySummary} RecoverySummary */

import { AlertCircle, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useRowSelection from '../hooks/useRowSelection';
import { reportBulkResult, runBulk } from '../services/bulkService';
import recoveryService from '../services/recoveryService';

const RecoveryPage = () => {
  const toast = useToast();
  const { showToast } = toast;
  /** @type {[RecoverySummary | null, Function]} */
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('products');
  const [deletedItems, setDeletedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const selection = useRowSelection(
    deletedItems.map((item) => item.id),
    { resetKey: activeTab },
  );

  const tabs = [
    { key: 'products', label: 'Products' },
    { key: 'orders', label: 'Orders' },
    { key: 'customers', label: 'Customers' },
    { key: 'vendors', label: 'Vendors' },
  ];

  const fetchSummary = useCallback(async () => {
    try {
      const data = await recoveryService.getRecoverySummary();
      setSummary(data);
    } catch (error) {
      showToast(error.message, 'error');
    }
  }, [showToast]);

  const fetchDeletedItems = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      switch (activeTab) {
        case 'products':
          data = await recoveryService.getDeletedProducts();
          break;
        case 'orders':
          data = await recoveryService.getDeletedOrders();
          break;
        case 'customers':
          data = await recoveryService.getDeletedCustomers();
          break;
        case 'vendors':
          data = await recoveryService.getDeletedVendors();
          break;
        default:
          data = [];
      }
      setDeletedItems(data);
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, showToast]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchDeletedItems();
  }, [fetchDeletedItems]);

  const handleRestore = async (itemId) => {
    if (!window.confirm('Are you sure you want to restore this item?')) return;

    try {
      switch (activeTab) {
        case 'products':
          await recoveryService.restoreProduct(itemId);
          break;
        case 'orders':
          await recoveryService.restoreOrder(itemId);
          break;
        case 'customers':
          await recoveryService.restoreCustomer(itemId);
          break;
        case 'vendors':
          await recoveryService.restoreVendor(itemId);
          break;
        default:
          break;
      }
      showToast('Item restored successfully', 'success');
      fetchDeletedItems();
      fetchSummary();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const handleBulkRestore = async () => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/recovery/bulk', {
        ids: selection.selectedIds,
        action: 'restore',
        params: { entity: activeTab },
      });
      reportBulkResult(toast, result, 'restored', 'item');
      setRestoreOpen(false);
      selection.clear();
      fetchDeletedItems();
      fetchSummary();
    } catch (error) {
      toast.error(error.message || 'Failed to restore items');
    } finally {
      setBulkBusy(false);
    }
  };

  const handlePermanentDelete = async (itemId) => {
    if (
      !window.confirm(
        '⚠️ WARNING: This will PERMANENTLY delete this item. This action CANNOT be undone. Are you absolutely sure?',
      )
    )
      return;

    try {
      switch (activeTab) {
        case 'products':
          await recoveryService.permanentlyDeleteProduct(itemId);
          break;
        case 'orders':
          await recoveryService.permanentlyDeleteOrder(itemId);
          break;
        case 'customers':
          await recoveryService.permanentlyDeleteCustomer(itemId);
          break;
        case 'vendors':
          await recoveryService.permanentlyDeleteVendor(itemId);
          break;
        default:
          break;
      }
      showToast('Item permanently deleted', 'success');
      fetchDeletedItems();
      fetchSummary();
    } catch (error) {
      showToast(error.message, 'error');
    }
  };

  const renderItemDetails = (item) => {
    switch (activeTab) {
      case 'products':
        return (
          <>
            <td className="px-6 py-4">
              <div className="flex items-center">
                {item.image_urls?.[0] && (
                  <img src={item.image_urls[0]} alt={item.name} className="w-12 h-12 object-cover rounded-lg mr-3" />
                )}
                <div>
                  <div className="font-medium text-foreground">{item.name}</div>
                  <div className="text-sm text-muted-foreground">{item.slug}</div>
                </div>
              </div>
            </td>
            <td className="px-6 py-4 text-sm text-foreground">₹{parseFloat(item.price).toLocaleString('en-IN')}</td>
          </>
        );
      case 'orders':
        return (
          <>
            <td className="px-6 py-4">
              <div className="font-medium text-foreground">{item.order_number}</div>
              <div className="text-sm text-muted-foreground">
                {[item.shipping_first_name, item.shipping_last_name].filter(Boolean).join(' ')}
              </div>
            </td>
            <td className="px-6 py-4 text-sm text-foreground">₹{parseFloat(item.total).toLocaleString('en-IN')}</td>
          </>
        );
      case 'customers':
        return (
          <>
            <td className="px-6 py-4">
              <div className="font-medium text-foreground">{item.name}</div>
              <div className="text-sm text-muted-foreground">{item.email}</div>
            </td>
            <td className="px-6 py-4 text-sm text-foreground">{item.phone || 'N/A'}</td>
          </>
        );
      case 'vendors':
        return (
          <>
            <td className="px-6 py-4">
              <div className="font-medium text-foreground">{item.vendor_name}</div>
              <div className="text-sm text-muted-foreground">{item.company_name}</div>
            </td>
            <td className="px-6 py-4">
              <span className="px-2 py-1 text-xs rounded-full bg-muted text-foreground">
                {item.vendor_type.replace('_', ' ')}
              </span>
            </td>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">Data Recovery</h1>
        <p className="text-muted-foreground mt-1">Restore or permanently delete soft-deleted items</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-card rounded-lg shadow p-4">
            <p className="text-sm text-muted-foreground mb-1">Products</p>
            <p className="text-2xl font-bold text-foreground">{summary.deleted_products}</p>
          </div>
          <div className="bg-card rounded-lg shadow p-4">
            <p className="text-sm text-muted-foreground mb-1">Orders</p>
            <p className="text-2xl font-bold text-foreground">{summary.deleted_orders}</p>
          </div>
          <div className="bg-card rounded-lg shadow p-4">
            <p className="text-sm text-muted-foreground mb-1">Customers</p>
            <p className="text-2xl font-bold text-foreground">{summary.deleted_customers}</p>
          </div>
          <div className="bg-card rounded-lg shadow p-4">
            <p className="text-sm text-muted-foreground mb-1">Vendors</p>
            <p className="text-2xl font-bold text-foreground">{summary.deleted_vendors}</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-950/50 rounded-lg shadow p-4">
            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Total Deleted</p>
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">{summary.total_deleted_records}</p>
          </div>
        </div>
      )}

      {/* Warning Alert */}
      <div className="bg-yellow-50 dark:bg-yellow-950/50 border-l-4 border-yellow-400 p-4 mb-6">
        <div className="flex">
          <AlertCircle className="text-yellow-400 flex-shrink-0" size={20} />
          <div className="ml-3">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              <strong>Note:</strong> Items shown here are soft-deleted and can be restored within 90 days. After 90
              days, they will be automatically permanently deleted.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card rounded-lg shadow overflow-hidden mb-6">
        <div className="flex border-b overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 whitespace-nowrap flex-1 px-6 py-4 text-sm font-medium transition ${
                activeTab === tab.key
                  ? 'border-b-2 border-foreground text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Items List */}
        <div className="overflow-x-auto">
          <div className="px-4 pt-4">
            <BulkActionBar count={selection.count} onClear={selection.clear}>
              <BulkActionBar.Button onClick={() => setRestoreOpen(true)}>Restore</BulkActionBar.Button>
            </BulkActionBar>
          </div>
          {loading ? (
            <LoadingScreen />
          ) : deletedItems.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">No deleted {activeTab} found</div>
          ) : (
            <table className="w-full">
              <thead className="bg-muted/50">
                <tr>
                  <th className="pl-6 py-3">
                    <HeaderCheckbox selection={selection} />
                  </th>
                  {renderItemDetails({}) && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Item
                    </th>
                  )}
                  {renderItemDetails({}) && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Details
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Deleted At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {deletedItems.map((item) => (
                  <tr key={item.id} className="hover:bg-muted/50">
                    <td className="pl-6 py-4">
                      <RowCheckbox selection={selection} id={item.id} label="Select deleted item" />
                    </td>
                    {renderItemDetails(item)}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                      {item.deleted_at ? new Date(item.deleted_at).toLocaleString('en-IN') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => handleRestore(item.id)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/80 transition mr-2"
                      >
                        <RefreshCw size={14} />
                        Restore
                      </button>
                      <button
                        onClick={() => handlePermanentDelete(item.id)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/80 transition"
                      >
                        <Trash2 size={14} />
                        Delete Forever
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {restoreOpen && (
        <BulkConfirmModal
          title={`Restore ${activeTab}`}
          message={
            activeTab === 'products'
              ? 'They return to the catalog. Restoring does not bring back the stock that deleting zeroed, so re-adjust it afterwards.'
              : 'They return to their normal lists.'
          }
          count={selection.count}
          confirmLabel="Restore"
          busy={bulkBusy}
          onConfirm={handleBulkRestore}
          onClose={() => setRestoreOpen(false)}
        />
      )}
    </div>
  );
};

export default RecoveryPage;
